import { validateMagnetFlow } from './magnetFlow.js';
import { MAGNET_HEALTH_RULE_VERSION, nonSubmittingExits } from './magnetCompletion.js';
export { MAGNET_HEALTH_RULE_VERSION };
const object = v => v && typeof v === 'object' && !Array.isArray(v);
const finding = (code, message, extra = {}) => ({ code, message, ...extra });

export function auditMagnetPackage(pkg) {
  try { return inspectPackage(pkg); }
  catch { return { status: 'not_checked', flows_checked: 0, incomplete: true,
    findings: [finding('malformed_definition', 'This definition could not be analyzed. Manual review is required.')] }; }
}

function inspectPackage(pkg) {
  const unknown = message => ({ status: 'not_checked', findings: [finding('not_checked', message)], flows_checked: 0 });
  if (!object(pkg)) return unknown('The conversation definition is missing or is not an object.');
  if (pkg.mag_ab?.enabled && !object(pkg.mag_ab.b)) return unknown('The enabled B variant has no valid definition.');
  const variants = [['A', pkg]];
  if (pkg.mag_ab?.enabled) variants.push(['B', { ...pkg, ...pkg.mag_ab.b }]);
  const findings = [];
  let flowsChecked = 0, nodes = 0, incomplete = false;
  for (const [variant, value] of variants) {
    if (value.mag_routes != null && !object(value.mag_routes)) return unknown('Named routes are not a valid object.');
    const routes = Object.entries(value.mag_routes || {});
    if (routes.length > 20) return unknown('This definition exceeds the supported route limit.');
    const flows = [['base', value.mag_macro_steps], ...routes.map(([name, steps]) => [`route:${name}`, steps])];
    for (const [route, original] of flows) {
      nodes += Array.isArray(original) ? original.length : 0;
      if (nodes > 2000) return unknown('This definition exceeds the supported total step limit.');
      const context = { variant, route };
      // Legacy linear flows may omit IDs; do not mistake this for broken routing.
      // Synthetic IDs exist only in this in-memory check and cannot become targets.
      const ids = new Set((Array.isArray(original) ? original : []).map(s => String(s?.id ?? '')));
      const steps = Array.isArray(original) ? original.map((s, i) => {
        if (!object(s) || String(s.id ?? '').trim()) return s;
        let id = `__audit_${i}`;
        while (ids.has(id) || original.some(x => Array.isArray(x?.step_options) && x.step_options.some(o => String(o?.goto) === id))) id += '_';
        ids.add(id); return { ...s, id };
      }) : original;
      const problem = validateMagnetFlow(steps, { allowUnsubmittedContacts: true });
      if (problem) {
        incomplete = true;
        findings.push({ ...problem, ...context, severity: 'review', category: 'compatibility',
          message: `${problem.message} Review against the served runtime before changing an existing flow.` });
        continue;
      }
      flowsChecked++;
      findings.push(...nonSubmittingExits(steps).filter(f => f.kind > 0).map(f => ({ ...f, ...context,
        severity: f.kind === 2 ? 'action_required' : 'review', category: 'completion' })));
    }
  }
  const status = findings.some(f => f.severity === 'action_required') ? 'action_required'
    : findings.length ? 'needs_review' : 'checked';
  return { status, findings, flows_checked: flowsChecked, incomplete,
    rule_version: MAGNET_HEALTH_RULE_VERSION };
}
